import React, { createContext, Dispatch, SetStateAction, useCallback, useContext } from "react";
import { QueryProps } from "@/api/api";
import { useStateSyncedWithQueryParam } from "@/dateRanges/UseInitialOverrideUnlessInQueryParamsOnFirstLoad";
import { Combobox } from "@/components/combo-box";

export interface AggregateNormalizeOption {
  label: string;
  value: string | boolean;
  unit?: string;
}

export const NONE: AggregateNormalizeOption = { label: "No normalization", value: false, unit: undefined };
export const OAT: AggregateNormalizeOption = { label: "Outdoor air temperature", value: "oat", unit: "°F" };
export const CDD: AggregateNormalizeOption = { label: "Cooling degree days", value: "cdd", unit: "CDD" };

export const AGGREGATE_NORMALIZE_OPTIONS = [
  NONE, OAT, CDD,
];

type AggregateNormalizeContextType = {
  aggregateNormalize: AggregateNormalizeOption;
  setAggregateNormalize: Dispatch<SetStateAction<AggregateNormalizeOption>>;
  queryWithAggregateNormalize: (query: QueryProps) => QueryProps;
  unitWithAggregateNormalize: (unit: string) => string;
};

const AggregateNormalizeContext = createContext<AggregateNormalizeContextType | undefined>(undefined);

export function AggregateNormalizeContextProvider({ children }: any) {

  const [aggregateNormalize, setAggregateNormalize] = useStateSyncedWithQueryParam<AggregateNormalizeOption>(
    "aggregateNormalize",
    NONE,
    (aggregateNormalizeOption) => aggregateNormalizeOption.value.toString(),
    (paramValue) => AGGREGATE_NORMALIZE_OPTIONS.find((_aggregateNormalizeOption) => paramValue && _aggregateNormalizeOption.value.toString() === paramValue)
  );

  const queryWithAggregateNormalize = useCallback(
    (query: QueryProps): QueryProps => ({
      ...query,
      normalize: aggregateNormalize !== NONE ? aggregateNormalize.value : undefined
    }),
    [aggregateNormalize]
  )

  const unitWithAggregateNormalize = useCallback(
    (unit: string): string => (aggregateNormalize === NONE ? unit : `${unit} / ${aggregateNormalize.unit}`),
    [aggregateNormalize]
  )

  return (
    <AggregateNormalizeContext.Provider
      value={{
        aggregateNormalize,
        setAggregateNormalize,
        queryWithAggregateNormalize,
        unitWithAggregateNormalize
      }}
    >
      {children}
    </AggregateNormalizeContext.Provider>
  );
}


export function useAggregateNormalizeContext() {
  const context = useContext(AggregateNormalizeContext);
  if (context === undefined) {
    throw new Error("Aggregate Normalize Context must be used within its Provider");
  }

  return context;
}

export const AggregateNormalizeSelector = () => {

  const { aggregateNormalize, setAggregateNormalize } = useAggregateNormalizeContext();

  return (
    <Combobox<AggregateNormalizeOption>
      className="bg-white w-fit px-2 whitespace-nowrap"
      placeholder="Normalize"
      options={AGGREGATE_NORMALIZE_OPTIONS}
      onSelectOption={(option) => setAggregateNormalize(option)}
      defaultValue={aggregateNormalize.value}
    />
  )
}
